/**
 * Import third-party libraries.
 */
import styled from 'styled-components';

/**
 * Import custom style properties.
 */
import { COLORS } from '../styles';

/**
 *
 * Chip styles.
 */
export const ChipContainer = styled.div`
  cursor: pointer;
  color: ${COLORS.white};
  display: inline-block;

  button {
    background: transparent;
    display: inline-block;
    padding: 0;
    width: auto;
    height: auto;
    color: inherit;

    div {
      color: inherit;
      width: auto;
      height: auto;

      svg {
        color: inherit;
      }
    }

    &:hover,
    &:hover:not(:disabled),
    :focus,
    :focus:not(:disabled) {
      background: transparent;
      display: inline-block;
      padding: 0;
      width: auto;
      height: auto;
      color: inherit;
      outline: none;

      div {
        box-shadow: none;
        background-color: transparent;

        svg {
          color: inherit;
        }
      }
    }
  }

  &.small {
    font-size: 14px;
    border-radius: 40px;
    padding: 4px 8px;

    svg {
      vertical-align: middle;

      &.icon {
        margin-right: 6px;
      }

      &.remove {
        margin-left: 7px;
      }
    }
  }

  &.medium {
    font-size: 16px;
    border-radius: 80px;
    padding: 10px 12px;

    svg {
      vertical-align: middle;

      &.icon {
        margin-right: 8px;
      }

      &.remove {
        margin-left: 10px;
      }
    }
  }

  &.large {
    font-size: 18px;
    border-radius: 120px;
    padding: 16px;

    svg {
      vertical-align: middle;

      &.icon {
        margin-right: 11px;
      }

      &.remove {
        margin-left: 13px;
      }
    }
  }

  &.disabled {
    cursor: not-allowed;
    background: ${COLORS.neutral_100};
    color: ${COLORS.neutral_300};

    &:hover {
      background: ${COLORS.neutral_100};
    }

    &:active {
      background: ${COLORS.neutral_100};
    }
  }

  &.normal {
    background: ${COLORS.primary_500};

    &:hover {
      background: ${COLORS.primary_700};
    }

    &:active {
      background: ${COLORS.primary_800};
    }
  }

  &.warning {
    color: ${COLORS.black};
    background: ${COLORS.warning_100};

    &:hover {
      background: ${COLORS.warning_400};
    }

    &:active {
      background: ${COLORS.warning_500};
    }
  }

  &.error {
    color: ${COLORS.black};
    background: ${COLORS.critical_100};

    &:hover {
      background: ${COLORS.critical_400};
    }

    &:active {
      background: ${COLORS.critical_500};
    }
  }
`;

export const ChipInputContainer = styled.div`
  min-height: 56px;
  height: auto;
  width: 100%;
  border-radius: 4px;
  border: 2px solid transparent;
  box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_300};
  background-color: #fff;
  box-sizing: border-box;
  padding: 16px 16px 8px 16px;
  box-sizing: border-box;
  width: 100%;
  cursor: text;
  outline: none;
  border-radius: 4.5px;
  display: inline-block;

  font-size: 18px;
  color: ${COLORS.black} !important;

  > div {
    margin-right: 8px;
    margin-bottom: 8px;
  }

  &.active {
    -webkit-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
    -moz-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
  }

  &:hover {
    -webkit-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_700};
    -moz-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_700};
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_700};
  }
`;

export const RequiredStar = styled.span`
  color: ${COLORS.critical_500};
  font-size: 28px;
  line-height: 12px;
  float: right;
  font-weight: 700;
  position: relative;
  top: 10px;
`;

export const FilterChipContainer = styled(ChipContainer)`
  &.normal {
    background: ${COLORS.white};
    color: ${COLORS.neutral_600};
    border: 2px solid;
    border-color: ${COLORS.neutral_200};

    &:hover {
      background: ${COLORS.primary_20};
      color: ${COLORS.primary_600};
      border-color: ${COLORS.primary_200};
    }

    &:active, &:focus {
      background: ${COLORS.primary_100};
      color: ${COLORS.primary_800};
      border-color: ${COLORS.primary_300};
      box-shadow: 0px 4px 12px ${COLORS.focus_25}, 0px 0px 8px ${COLORS.focus} !important;
    }
  }

  &.selected {
    background: ${COLORS.primary_500};
    color: ${COLORS.white};
    border: 2px solid;
    border-color: ${COLORS.primary_500};

    &:hover {
      background: ${COLORS.primary_700};
      color: ${COLORS.white};
      border-color: ${COLORS.primary_700};
    }

    &:active, &:focus {
      background: ${COLORS.primary_800};
      color: ${COLORS.white};
      border-color: ${COLORS.primary_800};
      box-shadow: 0px 4px 12px ${COLORS.focus_25}, 0px 0px 8px ${COLORS.focus} !important;
    }
  }
`;

export const ActionChipContainer = styled(ChipContainer)`
  &.normal {
    background: ${COLORS.white};
    color: ${COLORS.neutral_600};
    border: 2px solid;
    border-color: ${COLORS.neutral_200};

    &:hover {
      background: ${COLORS.primary_20};
      color: ${COLORS.primary_600};
      border-color: ${COLORS.primary_200};
    }

    &:active, &:focus {
      background: ${COLORS.primary_100};
      color: ${COLORS.primary_800};
      border-color: ${COLORS.primary_300};
      box-shadow: 0px 4px 12px ${COLORS.focus_25}, 0px 0px 8px ${COLORS.focus} !important;
    }
  }
`;

export const ChipInputEl = styled.input`
  height: 40px;
  width: auto;
  display: inline;
  border: none;
  outline: none;
  margin-top: 5px;
  margin-bottom: 8px;

  font-size: 18px;
  color: ${COLORS.black} !important;
`;